import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, X, ArrowRight, Calendar, Clock, ShieldCheck } from "lucide-react";
import { Section, FaqList, ResultCard } from "@/components";
import { Shell } from "@/pages_marketing";
import { Seo, http, SITE } from "@/lib";

export function Blog() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await http.get("/blog");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Shell>
      <Seo title="Blog" description="Guides on email verification, phone validation, IP intelligence, fraud prevention and data quality from the Verix team." path="/blog" />
      <Section className="py-20">
        <h1 className="font-display font-black text-5xl tracking-tighter mb-4">The Verix Journal</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mb-12">Field notes on verification, deliverability and stopping fraud.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {items.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="bg-card p-7 hover:bg-secondary transition-colors group" data-testid={`blog-card-${p.slug}`}>
              <span className="text-xs font-mono uppercase tracking-widest text-accent">{p.category}</span>
              <h3 className="font-display font-bold text-xl mt-2 mb-2 group-hover:text-accent leading-snug">{p.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{p.excerpt}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground"><Calendar size={13} /> {p.date} <Clock size={13} /> {p.read_time}</div>
            </Link>
          ))}
        </div>
      </Section>
    </Shell>
  );
}

function renderBody(body) {
  return body.split("\n").map((line, i) => {
    const k = `${i}-${line.slice(0, 24)}`;
    if (line.startsWith("### ")) return <h3 key={k} className="font-display font-bold text-xl mt-6 mb-2">{line.slice(4)}</h3>;
    if (line.startsWith("## ")) return <h2 key={k} className="font-display font-bold text-2xl mt-8 mb-3">{line.slice(3)}</h2>;
    if (line.startsWith("- ")) return <li key={k} className="ml-5 list-disc text-muted-foreground">{inline(line.slice(2))}</li>;
    if (/^\d+\. /.test(line)) return <li key={k} className="ml-5 list-decimal text-muted-foreground">{inline(line.replace(/^\d+\. /, ""))}</li>;
    if (line.trim() === "---") return <hr key={k} className="my-6 border-border" />;
    if (line.trim() === "") return null;
    return <p key={k} className="text-muted-foreground leading-relaxed my-3">{inline(line)}</p>;
  });
}
function inline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    const k = `${i}-${p.slice(0, 16)}`;
    if (p.startsWith("**")) return <strong key={k} className="text-foreground">{p.slice(2, -2)}</strong>;
    const m = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) return <Link key={k} to={m[2]} className="text-accent link-underline">{m[1]}</Link>;
    return <span key={k}>{p}</span>;
  });
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notfound, setNf] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await http.get(`/blog/${slug}`); setP(data); }
    catch { setNf(true); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  if (notfound) return <Shell><Section className="py-32 text-center"><h1 className="font-display text-3xl">Post not found</h1><Link to="/blog" className="text-accent">← Back to blog</Link></Section></Shell>;
  if (!p) return <Shell><Section className="py-32 text-center text-muted-foreground">Loading…</Section></Shell>;
  return (
    <Shell>
      <Seo title={p.title} description={p.excerpt} path={`/blog/${slug}`} type="article"
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: p.title, description: p.excerpt, datePublished: p.date, author: { "@type": "Organization", name: p.author }, publisher: { "@type": "Organization", name: "Verix" } }} />
      <Section className="py-16 max-w-2xl">
        <Link to="/blog" className="text-sm text-muted-foreground hover:text-accent">← The Verix Journal</Link>
        <span className="block text-xs font-mono uppercase tracking-widest text-accent mt-6">{p.category}</span>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 mb-4 leading-tight">{p.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground border-b border-border pb-6 mb-6">
          <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-accent" /> {p.author}</span>
          <span className="flex items-center gap-1.5"><Calendar size={14} /> {p.date}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> {p.read_time}</span>
        </div>
        <div className="border-l-2 border-accent bg-card p-4 mb-8">
          <span className="text-xs font-bold uppercase tracking-widest text-accent">TL;DR</span>
          <p className="text-sm text-foreground/90 mt-1">{p.tldr}</p>
        </div>
        <article>{renderBody(p.body)}</article>
        <div className="mt-12 border border-accent p-8 text-center">
          <h3 className="font-display font-bold text-2xl mb-2">Verify your data with Verix</h3>
          <p className="text-muted-foreground mb-5">Email, phone and IP risk in one API. Free tier, no card.</p>
          <Link to="/signup" className="btn-accent px-6 py-3 inline-flex items-center gap-2">Start free <ArrowRight size={16} /></Link>
        </div>
      </Section>
    </Shell>
  );
}

export function Compare() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await http.get("/competitors");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Shell>
      <Seo title="Verix vs The Competition" description="Honest side-by-side comparisons of Verix against Twilio Lookup, ZeroBounce, IPQualityScore, NeverBounce, Hunter.io and more verification tools." path="/compare" />
      <Section className="py-20">
        <h1 className="font-display font-black text-5xl tracking-tighter mb-4">Verix vs everyone.</h1>
        <p className="text-muted-foreground text-lg max-w-2xl mb-12">Honest, head-to-head comparisons. Where they're strong, where we win.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {items.map((c) => (
            <Link key={c.slug} to={`/vs/${c.slug}`} className="bg-card p-6 hover:bg-secondary transition-colors group" data-testid={`compare-${c.slug}`}>
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{c.category}</span>
              <h3 className="font-display font-bold text-xl mt-1 group-hover:text-accent">Verix vs {c.name}</h3>
              <p className="text-sm text-muted-foreground mt-2">{c.tagline}</p>
              <span className="text-accent text-sm flex items-center gap-1 mt-4">Compare <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </Section>
    </Shell>
  );
}

function VsColumns({ c }) {
  return (
    <div className="grid md:grid-cols-2 gap-6 mt-12">
      <div className="border border-border bg-card p-6">
        <h2 className="font-display font-bold text-xl mb-4">Where {c.name} is strong</h2>
        <ul className="space-y-3">{c.their_strengths.map((s) => <li key={s} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="text-muted-foreground mt-0.5 shrink-0" size={16} /> {s}</li>)}</ul>
      </div>
      <div className="border border-accent bg-card p-6">
        <h2 className="font-display font-bold text-xl mb-4 text-accent">Where Verix wins</h2>
        <ul className="space-y-3">{c.where_we_win.map((s) => <li key={s} className="flex items-start gap-2 text-sm"><Check className="text-valid mt-0.5 shrink-0" size={16} /> {s}</li>)}</ul>
      </div>
    </div>
  );
}

function VsFeatureTable({ c }) {
  return (
    <div className="border border-border" data-testid="comparison-table">
      <div className="grid grid-cols-[1fr_auto_auto] text-sm">
        <div className="p-3 font-display font-bold border-b border-border">Feature</div>
        <div className="p-3 font-display font-bold border-b border-l border-border text-accent text-center min-w-[120px]">Verix</div>
        <div className="p-3 font-display font-bold border-b border-l border-border text-muted-foreground text-center min-w-[120px]">{c.name}</div>
        {c.feature_table.map((r) => (
          <React.Fragment key={r.feature}>
            <div className="p-3 border-b border-border text-muted-foreground">{r.feature}</div>
            <div className="p-3 border-b border-l border-border text-center font-mono text-xs">{r.verix}</div>
            <div className="p-3 border-b border-l border-border text-center font-mono text-xs text-muted-foreground">{r.them}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export function Vs() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [nf, setNf] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await http.get(`/competitors/${slug}`); setC(data); }
    catch { setNf(true); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  if (nf) return <Shell><Section className="py-32 text-center"><h1 className="font-display text-3xl">Comparison not found</h1><Link to="/compare" className="text-accent">← All comparisons</Link></Section></Shell>;
  if (!c) return <Shell><Section className="py-32 text-center text-muted-foreground">Loading…</Section></Shell>;
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  return (
    <Shell>
      <Seo title={`Verix vs ${c.name} — Honest Comparison`} description={`Verix vs ${c.name}: feature and pricing comparison. ${c.tagline} See where each tool wins and why Verix is a strong ${c.name} alternative.`} path={`/vs/${slug}`} jsonLd={faqLd} />
      <Section className="py-16 max-w-4xl">
        <Link to="/compare" className="text-sm text-muted-foreground hover:text-accent">← All comparisons</Link>
        <span className="block text-xs font-mono uppercase tracking-widest text-accent mt-6">{c.category}</span>
        <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mt-2 mb-4">Verix vs {c.name}</h1>
        <p className="text-muted-foreground text-lg max-w-2xl">{c.tagline} Here's an honest breakdown — and why teams pick Verix as a {c.name} alternative.</p>

        <VsColumns c={c} />

        <h2 className="font-display font-bold text-3xl mt-16 mb-6">Feature comparison</h2>
        <VsFeatureTable c={c} />

        <div className="grid sm:grid-cols-2 gap-6 mt-10">
          <div className="border border-accent p-6"><p className="text-xs uppercase tracking-widest text-accent mb-1">Verix pricing</p><p className="font-display font-bold text-lg">{c.pricing.verix}</p></div>
          <div className="border border-border p-6"><p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{c.name} pricing</p><p className="font-display font-bold text-lg text-muted-foreground">{c.pricing.them}</p></div>
        </div>

        <h2 className="font-display font-bold text-3xl mt-16 mb-6">FAQ</h2>
        <FaqList faqs={c.faqs} />

        <div className="mt-12 border border-accent p-8 text-center">
          <h3 className="font-display font-bold text-2xl mb-2">Try Verix free — see the difference</h3>
          <p className="text-muted-foreground mb-5">50 verifications/mo, no card. Email, phone and IP in one credit.</p>
          <Link to="/signup" className="btn-accent px-6 py-3 inline-flex items-center gap-2">Start free <ArrowRight size={16} /></Link>
        </div>
      </Section>
    </Shell>
  );
}

export function ResultPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await http.get(`/result/${id}`); setData(data); }
    catch { setNf(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (nf) return <Shell><Section className="py-32 text-center"><h1 className="font-display text-3xl">Result not found</h1><Link to="/" className="text-accent">Make your own →</Link></Section></Shell>;
  if (!data) return <Shell><Section className="py-32 text-center text-muted-foreground">Loading…</Section></Shell>;
  const r = data.result;
  return (
    <Shell>
      <Seo title={`${r.type} verification result — ${r.status.toUpperCase()}`} description={`Verix verified this ${r.type} as ${r.status} with a trust score of ${r.score}/100. Run your own free verification.`} path={`/r/${id}`} />
      <Section className="py-20 max-w-xl">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">Verix verification result</span>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2">Shared result</h1>
        </div>
        <ResultCard result={{ ...r, public_id: id }} />
        <div className="mt-10 border border-accent p-8 text-center">
          <h3 className="font-display font-bold text-2xl mb-2">Make your own</h3>
          <p className="text-muted-foreground mb-5">Verify any email, phone or IP free with Verix.</p>
          <Link to="/" className="btn-accent px-6 py-3 inline-flex items-center gap-2">Verify free <ArrowRight size={16} /></Link>
        </div>
      </Section>
    </Shell>
  );
}
